/**
 * Symbology validators and helpers. Pure functions, zero dependencies.
 */
import type { Symbology } from "./types";

/**
 * Sensible upper bound for post-decode CODE128 payloads. GS1-128 caps a
 * symbol's data at 48 characters; free-form Code 128 labels longer than that
 * are almost certainly mis-scans or pasted garbage.
 */
export const CODE128_MAX_LENGTH = 48;

const DIGITS_RE = /^[0-9]+$/;
// Printable ASCII, space (0x20) through tilde (0x7E).
const PRINTABLE_ASCII_RE = /^[\x20-\x7E]+$/;

/**
 * GS1 mod-10 check digit for a GTIN payload (the digits WITHOUT the final
 * check digit). Shared by EAN-13, UPC-A and GTIN-14 — the algorithm anchors
 * its 3/1 weights at the check-digit position and walks right-to-left, which
 * is why one implementation serves all lengths (and why left zero-padding
 * never changes the check digit).
 *
 * @param payloadDigits e.g. `"400638133393"` for the EAN-13 `4006381333931`.
 * @throws RangeError if the input is empty or contains a non-digit.
 */
export function gs1CheckDigit(payloadDigits: string): number {
  if (!DIGITS_RE.test(payloadDigits)) {
    throw new RangeError(
      `gs1CheckDigit expects a non-empty digit string, got ${JSON.stringify(payloadDigits)}`,
    );
  }
  let sum = 0;
  // Walk right-to-left; the digit adjacent to the check position weighs 3.
  for (let i = 0; i < payloadDigits.length; i++) {
    const digit = payloadDigits.charCodeAt(payloadDigits.length - 1 - i) - 48;
    sum += digit * (i % 2 === 0 ? 3 : 1);
  }
  return (10 - (sum % 10)) % 10;
}

/** True when `value` is `length` digits with a valid GS1 mod-10 check digit. */
function isValidGtin(value: string, length: number): boolean {
  if (value.length !== length || !DIGITS_RE.test(value)) return false;
  const payload = value.slice(0, -1);
  const check = value.charCodeAt(value.length - 1) - 48;
  return gs1CheckDigit(payload) === check;
}

/** EAN-13: exactly 13 digits, valid GS1 mod-10 check digit. */
export function isValidEan13(value: string): boolean {
  return isValidGtin(value, 13);
}

/** UPC-A: exactly 12 digits, valid GS1 mod-10 check digit. */
export function isValidUpcA(value: string): boolean {
  return isValidGtin(value, 12);
}

/** GTIN-14: exactly 14 digits, valid GS1 mod-10 check digit. */
export function isValidGtin14(value: string): boolean {
  return isValidGtin(value, 14);
}

/**
 * CODE128 post-decode validation: charset/length ONLY — deliberately no check
 * digit.
 *
 * The Code 128 symbol checksum (mod-103) is part of the barcode TRANSPORT:
 * the scanner verifies it while decoding the bars and STRIPS it before
 * emitting keyboard output, so it never reaches this code. Inventing a fake
 * post-decode check digit here would reject valid labels. What we CAN check
 * after decode: non-empty, printable ASCII (Code 128 encodes ASCII 0–127;
 * control characters never belong in item barcodes), and a sensible maximum
 * length ({@link CODE128_MAX_LENGTH}).
 */
export function isValidCode128(value: string): boolean {
  return value.length > 0 && value.length <= CODE128_MAX_LENGTH && PRINTABLE_ASCII_RE.test(value);
}

/**
 * Best-match symbology detection for a decoded value:
 * - 12 digits + valid check digit → `UPCA`
 * - 13 digits + valid check digit → `EAN13`
 * - 14 digits + valid check digit → `GTIN14`
 * - anything else printable-ASCII (≤ {@link CODE128_MAX_LENGTH}) → `CODE128`
 *   (including all-digit strings whose GS1 check digit is WRONG — Code 128
 *   can legitimately carry arbitrary digit runs)
 * - otherwise → `null`
 */
export function detectSymbology(value: string): Symbology | null {
  if (isValidUpcA(value)) return "UPCA";
  if (isValidEan13(value)) return "EAN13";
  if (isValidGtin14(value)) return "GTIN14";
  if (isValidCode128(value)) return "CODE128";
  return null;
}

/**
 * Optional normalization helper: left-zero-pads a valid UPC-A (12) or EAN-13
 * (13) to the 14-digit GTIN-14 form; passes a valid GTIN-14 through
 * unchanged. Returns `null` for anything else.
 *
 * Zero-padding preserves the check digit: GS1 weights anchor at the check
 * digit and run right-to-left, so existing digits keep their weights and the
 * prepended zeros contribute nothing.
 */
export function zeroPadToGtin14(value: string): string | null {
  if (isValidUpcA(value) || isValidEan13(value)) return value.padStart(14, "0");
  if (isValidGtin14(value)) return value;
  return null;
}
